import { Button } from "../ui/button";
import { ProductCard } from "../Product/ProductCard";
import { useNewArrivals } from "@nusrif/hooks";
import { useNavigate } from "react-router";
import { getLowestPriceVariant, isProductInStock } from "@nusrif/services";
import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const NewArrivals = () => {
  const navigate = useNavigate();
  const { products: newArrivals, isLoading, error } = useNewArrivals(50);
  // Use newArrivals directly (no shuffle)
  const shuffledArrivals = newArrivals;
  // Group by fit
  const { oversizeArrivals, boxArrivals, regularArrivals } = useMemo(() => {
    const normalize = (v?: string) => (v || "").trim().toLowerCase();
    const out = {
      oversizeArrivals: [] as typeof shuffledArrivals,
      boxArrivals: [] as typeof shuffledArrivals,
      regularArrivals: [] as typeof shuffledArrivals,
    };
    for (const p of shuffledArrivals) {
      const fit = normalize(p.customFields?.fit);
      if (fit.includes("oversize") || fit.includes("oversized")) out.oversizeArrivals.push(p);
      else if (fit.includes("box")) out.boxArrivals.push(p);
      else if (fit.includes("regular")) out.regularArrivals.push(p);
    }
    return out;
  }, [shuffledArrivals]);

  // Active section and refs
  const [activeSection, setActiveSection] = useState<"oversize" | "box" | "regular">("oversize");
  const oversizeRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const regularRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sections: Array<{ key: "oversize" | "box" | "regular"; ref: React.RefObject<HTMLDivElement | null> }> = [
      { key: "oversize", ref: oversizeRef },
      { key: "box", ref: boxRef },
      { key: "regular", ref: regularRef },
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const target = visible[0].target as HTMLDivElement;
          const matched = sections.find((s) => s.ref.current === target);
          if (matched) setActiveSection(matched.key);
        }
      },
      { root: null, threshold: [0.25, 0.5, 0.75] }
    );
    sections.forEach((s) => s.ref.current && observer.observe(s.ref.current));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((section: "oversize" | "box" | "regular") => {
    const map: Record<string, HTMLDivElement | null> = {
      oversize: oversizeRef.current,
      box: boxRef.current,
      regular: regularRef.current,
    };
    const node = map[section];
    if (node) {
      const topOffset = 100;
      const rect = node.getBoundingClientRect();
      const absoluteTop = window.scrollY + rect.top - topOffset;
      window.scrollTo({ top: absoluteTop, behavior: "smooth" });
      setActiveSection(section);
    }
  }, []);

  // Tabs fixing on scroll
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsAnchorYRef = useRef<number>(0);
  const [isTabsFixed, setIsTabsFixed] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!tabsContainerRef.current) return;
      const rect = tabsContainerRef.current.getBoundingClientRect();
      tabsAnchorYRef.current = window.scrollY + rect.top; // initial anchor position
      setTabsHeight(tabsContainerRef.current.offsetHeight || 48);
    };
    // measure after mount and on resize
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const shouldFix = window.scrollY >= tabsAnchorYRef.current - 1;
      setIsTabsFixed(shouldFix);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const handleFavoriteClick = (productId: number) => {
    console.log(`Favorite clicked for product ${productId}`);
    // Add your favorite logic here
  };
  const handleProductClick = (productId: number) => {
    console.log(`Product clicked: ${productId}`);
    navigate(`/product/${productId}`);
  };
  const handleViewAllClick = () => {
    console.log("View All clicked");
    navigate("/store");
    // Navigate to products page or filter by new arrivals
  };
  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-black">New Arrivals</h2>
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={handleViewAllClick}
          >
            <span className="font-semibold text-gray-600 text-[11px] hover:text-gray-800 transition-colors">
              View All
            </span>
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="w-full h-[250px] bg-gray-200 rounded-lg mb-3"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // Show error state
  if (error) {
    return (
      <div className="w-full space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-black">New Arrivals</h2>
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={handleViewAllClick}
          >
            <span className="font-semibold text-gray-600 text-[11px] hover:text-gray-800 transition-colors">
              View All
            </span>
          </Button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-500">Failed to load new arrivals</p>
          <Button
            variant="outline"
            className="mt-2"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  const renderGrid = (list: any[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 bg-gray-50">
      {list.map((product: any) => {
        const lowestPriceVariant = getLowestPriceVariant(product);
        const price = lowestPriceVariant
          ? formatCurrency(
              lowestPriceVariant.priceWithTax,
              lowestPriceVariant.currencyCode
            )
          : formatCurrency(0, config.region.currencyCode);
        const mrpPrice = product.customFields.mrpPrice
          ? formatCurrency(
              product.customFields.mrpPrice,
              config.region.currencyCode
            )
          : formatCurrency(0, config.region.currencyCode);

        const image =
          product.featuredAsset?.preview ||
          product.assets[0]?.preview ||
          "/image.png";
        const brand =
          product.facetValues.find((f: any) => f.facet.name === "Brand")?.name ||
          "Two & Five";
        const name = product.name;
        const isInStock = isProductInStock(product);
        const productId = parseInt(product.id) || 0; // Convert string ID to number
        return (
          <ProductCard
            key={product.id}
            id={productId}
            image={image}
            brand={brand}
            name={name}
            price={price}
            mrpPrice={mrpPrice}
            heartIcon={isInStock ? "/love.png" : "/love-1.png"}
            onFavoriteClick={handleFavoriteClick}
            onProductClick={handleProductClick}
          />
        );
      })}
    </div>
  );

  return (
    <div className="w-full space-y-6">
      {/* Header with tagline and action */}
      <div className="flex items-end justify-between mt-8">
        <div className="space-y-1">
          <h1 className="text-base font-semibold text-black">Our Exclusive T‑Shirt Collection</h1>
          <p className="text-[11px] text-gray-500">Oversize, Box, and Regular fits curated for you</p>
        </div>
        <div className="pl-4">
          <Button variant="link" className="h-auto p-0" onClick={handleViewAllClick}>
            <span className="font-semibold text-primary-blue text-[11px] hover:text-primary-blue/80 transition-colors">
              View All
            </span>
          </Button>
        </div>
      </div>
      {/* Segmented tab bar (becomes fixed on scroll) */}
      <div ref={tabsContainerRef} className="-mx-4">
        <div
          className={
            (isTabsFixed
              ? "fixed inset-x-0 top-0 z-30 "
              : "relative z-10 ") +
            "bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
          }
        >
          <div className="px-4 py-2">
            <SegmentedTabs
              activeKey={activeSection}
              counts={{ oversize: oversizeArrivals.length, box: boxArrivals.length, regular: regularArrivals.length }}
              onSelect={scrollToSection}
            />
          </div>
        </div>
      </div>
      {/* Spacer only when fixed */}
      {isTabsFixed && <div style={{ height: tabsHeight }} />}

      {/* Sections */}
      {oversizeArrivals.length > 0 && (
        <div ref={oversizeRef} className="space-y-4 scroll-mt-28">
          <h3 className="text-base font-semibold text-black">Oversize Fit</h3>
          {renderGrid(oversizeArrivals)}
        </div>
      )}
      {boxArrivals.length > 0 && (
        <div ref={boxRef} className="space-y-4 scroll-mt-28">
          <h2 className="text-base font-semibold text-black">Boxy Fit</h2>
          {renderGrid(boxArrivals)}
        </div>
      )}
      {regularArrivals.length > 0 && (
        <div ref={regularRef} className="space-y-4 scroll-mt-28">
          <h3 className="text-base font-semibold text-black">Regular Fit</h3>
          {renderGrid(regularArrivals)}
        </div>
      )}
    </div>
  );
};

// SegmentedTabs component (local)
function SegmentedTabs({
  activeKey,
  counts,
  onSelect,
}: {
  activeKey: "oversize" | "box" | "regular";
  counts: { oversize: number; box: number; regular: number };
  onSelect: (key: "oversize" | "box" | "regular") => void;
}) {
  const tabs = [
    { key: "oversize" as const, label: "Oversize", count: counts.oversize },
    { key: "box" as const, label: "Boxy Fit", count: counts.box },
    { key: "regular" as const, label: "Regular", count: counts.regular },
  ];
  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === activeKey)
  );
  const widthPercent = 100 / tabs.length;
  return (
    <div role="tablist" aria-label="New Arrivals Sections" className="relative w-full select-none">
      <div className="grid grid-cols-3 text-sm font-medium text-gray-700 bg-gray-50 rounded-xl p-1 border border-gray-100">
        {tabs.map((tab) => {
          const isDisabled = tab.count === 0;
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelect(tab.key)}
              className={
                "relative py-2 text-center rounded-lg transition-colors " +
                (isDisabled
                  ? "text-gray-400"
                  : isActive
                  ? "text-primary-blue bg-white shadow-sm"
                  : "text-gray-700 hover:text-primary-blue")
              }
            >
              <span>{tab.label}</span>
              <span className="ml-1 text-xs opacity-70">{tab.count}</span>
            </button>
          );
        })}
      </div>
      {/* Animated underline */}
      <div
        className="pointer-events-none absolute -bottom-2 left-0 h-0.5 bg-primary-blue/80 transition-transform duration-300"
        style={{ width: `${widthPercent}%`, transform: `translateX(${activeIndex * 100}%)` }}
      />
    </div>
  );
}
